// InspirationSynthesis.jsx - Version avec import conditionnel
import React, { useState } from 'react';
import { 
  CircularProgress,
  Chip,
  Button,
  Alert
} from '@mui/material';
import { 
  BookOpen, 
  CheckCircle, 
  ArrowRight,
  Globe,
  TrendingUp,
  ExternalLink,
  RefreshCw,
  Search,
  Eye,
  Clock,
  Languages,
  MapPin,
  Share2
} from 'lucide-react';
import SynthesisCard from '../CP-Search/SynthesisCard';

// Configuration des langues par pays
const COUNTRY_LANGUAGE_CONFIG = {
  'MA': { name: 'Maroc' },
  'FR': { name: 'France' },
  'US': { name: 'États-Unis' },
  'CA': { name: 'Canada' },
  'ES': { name: 'Espagne' }
};

const InspirationSynthesis = ({ 
  synthesis,
  selectedSources,
  metadata,
  searchParams,
  loadingStatus,
  onBackToSearch,
  onBackToResults,
  onContinue,
  onNewSearch,
  currentCompetence,
  userObjective
}) => {
  // État pour le modal de partage
  const [showShareModal, setShowShareModal] = useState(false);
  const [ShareModalComponent, setShareModalComponent] = useState(null);

  // Import conditionnel du modal
  const loadShareModal = async () => {
    if (!ShareModalComponent) {
      try {
        const { default: ShareSynthesisModal } = await import('./ShareSynthesisModal');
        setShareModalComponent(() => ShareSynthesisModal);
        setShowShareModal(true);
      } catch (error) {
        console.error('Erreur lors du chargement du modal de partage:', error);
        alert('Erreur lors du chargement du module de partage');
      }
    } else {
      setShowShareModal(true);
    }
  };

  // Fonctions utilitaires (inchangées)
  const getSourceTypeColor = (type) => {
    const colors = {
      'Educational': 'bg-blue-100 text-blue-800',
      'Media': 'bg-orange-100 text-orange-800',
      'Non-profit': 'bg-green-100 text-green-800',
      'Commercial': 'bg-purple-100 text-purple-800',
      'Governmental': 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getInspirationScoreColor = (score) => {
    if (score >= 8) return 'bg-green-100 text-green-800';
    if (score >= 6) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const getProvidersText = () => {
    if (!metadata || !metadata.providers) return 'N/A';
    if (!Array.isArray(metadata.providers)) {
      if (typeof metadata.providers === 'string') return metadata.providers;
      return 'N/A';
    }
    if (metadata.providers.length === 0) return 'N/A';
    return metadata.providers.join(', ');
  };

  const calculateAverageScore = () => {
    if (selectedSources.length === 0) return 0;
    const total = selectedSources.reduce((acc, source) => 
      acc + (source.inspirationScore || 5), 0
    );
    return (total / selectedSources.length).toFixed(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  // COMPOSANTS
  const SynthesisHeader = () => (
    <div className="space-y-4">
      <div>
        <h3 className="text-xl font-semibold text-gray-800 flex items-center">
          <BookOpen className="h-6 w-6 mr-2 text-green-600" />
          Votre synthèse d'inspiration
        </h3>
        <p className="text-gray-600 mt-1">
          Basée sur {selectedSources.length} sources sélectionnées
          {synthesis?.inspirationScore && (
            <> • Score moyen: {synthesis.inspirationScore.toFixed(1)}/10</>
          )}
        </p>
        {synthesis?.id && (
          <p className="text-green-600 text-sm mt-1 flex items-center">
            <CheckCircle className="h-4 w-4 mr-1" />
            Synthèse sauvegardée automatiquement
          </p>
        )}
      </div>
      
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <Button
          onClick={onNewSearch || onBackToSearch}
          variant="outlined"
          startIcon={<Search />}
          className="text-gray-600 border-gray-300 hover:bg-gray-50 w-full sm:w-auto"
          sx={{ textTransform: 'none' }}
        >
          <span className="sm:hidden">Nouvelle recherche</span>
          <span className="hidden sm:inline">Rechercher</span>
        </Button>
        
        {selectedSources.length > 0 && (
          <Button
            onClick={onBackToResults}
            variant="outlined"
            startIcon={<Eye />}
            className="text-blue-600 border-blue-300 hover:bg-blue-50 w-full sm:w-auto"
            sx={{ textTransform: 'none' }}
          >
            <span className="sm:hidden">Consulter</span>
            <span className="hidden sm:inline">Consulter</span>
          </Button>
        )}

        {synthesis && (
          <Button
            onClick={loadShareModal}
            variant="outlined"
            startIcon={<Share2 />}
            className="text-purple-600 border-purple-300 hover:bg-purple-50 w-full sm:w-auto"
            sx={{ textTransform: 'none' }}
          >
            <span className="sm:hidden">Partager</span>
            <span className="hidden sm:inline">Partager </span>
          </Button>
        )}
        
        <Button
          onClick={onContinue}
          variant="contained"
          endIcon={<ArrowRight />}
          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
          sx={{ textTransform: 'none' }}
        >
          Continuer
        </Button>
      </div>
    </div>
  );

  const LoadingState = () => (
    <div className="flex justify-center items-center p-8 bg-white rounded-xl shadow-md mb-8">
      <CircularProgress className="text-blue-600" />
      <div className="ml-3">
        <p className="text-blue-700 font-medium">
          Génération et sauvegarde automatique en cours...
        </p>
        <p className="text-blue-600 text-sm">
          Création de votre synthèse d'inspiration personnalisée...
        </p>
      </div>
    </div>
  );

  const SynthesisDisplay = () => {
    if (!synthesis) return null;
    return (
      <div className="mb-6">
        <SynthesisCard synthesis={synthesis} />
      </div>
    );
  };

  const SourcesUsedSection = () => {
    if (selectedSources.length === 0) return null;

    return (
      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-4 flex items-center">
          <Globe className="h-5 w-5 mr-2" />
          Sources d'inspiration utilisées
          <Chip 
            label={`${selectedSources.length} sources`}
            size="small"
            className="ml-3 bg-blue-100 text-blue-800"
          />
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-white p-3 rounded border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Score moyen</span>
              <div className="flex items-center">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="font-bold text-green-600">{calculateAverageScore()}/10</span>
              </div>
            </div>
          </div>
                    
          <div className="bg-white p-3 rounded border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Domaines uniques</span>
              <span className="font-medium text-gray-800">
                {[...new Set(selectedSources.map(s => s.domain))].length}
              </span>
            </div>
          </div>
        </div>

        <div className="grid gap-3">
          {selectedSources.map((source, index) => (
            <div 
              key={index} 
              className="bg-white p-4 rounded border border-gray-200 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h5 className="font-medium text-gray-800 text-sm mr-3">
                      {source.title || source.domain}
                    </h5>
                    <div className="flex items-center gap-2">
                      <Chip 
                        label={source.type || 'Web'} 
                        size="small" 
                        className={`${getSourceTypeColor(source.type)} text-xs`}
                      />
                      {source.inspirationScore && (
                        <Chip 
                          label={`${source.inspirationScore}/10`}
                          size="small"
                          className={`${getInspirationScoreColor(source.inspirationScore)} text-xs`}
                        />
                      )}
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-xs mb-2 line-clamp-2">
                    {source.description?.substring(0, 120)}
                    {source.description?.length > 120 && '...'}
                  </p>
                  
                  <div className="flex items-center text-xs text-gray-500 gap-3">
                    <span className="flex items-center">
                      <Globe className="h-3 w-3 mr-1" />
                      {source.domain}
                    </span>
                    {source.language && (
                      <span className="uppercase font-medium">
                        {source.language}
                      </span>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => window.open(source.usableUrl || source.url || `https://${source.domain}`, '_blank')}
                  className="ml-3 text-blue-600 hover:text-blue-800 text-sm flex items-center"
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Visiter
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const SearchMetadataSection = () => {
    if (!metadata || !metadata.searchQuery) return null;

    return (
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-800 mb-3 flex items-center">
          <Search className="h-4 w-4 mr-2" />
          Détails de la recherche
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex items-start">
              <span className="font-medium text-blue-700 w-20 flex-shrink-0">Recherche :</span>
              <span className="text-blue-600">"{metadata.searchQuery}"</span>
            </div>
            
            <div className="flex items-center">
              <span className="font-medium text-blue-700 w-20 flex-shrink-0">Sources :</span>
              <span className="text-blue-600">{getProvidersText()}</span>
            </div>
            
            {synthesis?.datePublished && (
              <div className="flex items-center">
                <Clock className="h-3 w-3 mr-1 text-blue-500" />
                <span className="font-medium text-blue-700 w-19 flex-shrink-0">Créée :</span>
                <span className="text-blue-600 text-xs">{formatDate(synthesis.datePublished)}</span>
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            {searchParams && (
              <>
                <div className="flex items-center">
                  <MapPin className="h-3 w-3 mr-1 text-blue-500" />
                  <span className="font-medium text-blue-700 w-16 flex-shrink-0">Région :</span>
                  <span className="text-blue-600">
                    {searchParams.country ? (
                      COUNTRY_LANGUAGE_CONFIG[searchParams.country]?.name || searchParams.country
                    ) : 'N/A'}
                  </span>
                </div>
                
                <div className="flex items-center">
                  <Languages className="h-3 w-3 mr-1 text-blue-500" />
                  <span className="font-medium text-blue-700 w-16 flex-shrink-0">Langues :</span>
                  <span className="text-blue-600">
                    {searchParams.languages && Array.isArray(searchParams.languages) 
                      ? searchParams.languages.join(', ').toUpperCase()
                      : 'N/A'
                    }
                  </span>
                </div>
              </>
            )}
            
            <div className="flex items-center">
              <TrendingUp className="h-3 w-3 mr-1 text-blue-500" />
              <span className="font-medium text-blue-700 w-16 flex-shrink-0">Score :</span>
              <span className="text-blue-600 font-medium">{calculateAverageScore()}/10</span>
            </div>
          </div>
        </div>
        
        {searchParams?.enableGeographicLocalization && (
          <div className="mt-3 pt-3 border-t border-blue-200">
            <p className="text-blue-600 text-xs flex items-center">
              <MapPin className="h-3 w-3 mr-1" />
              <span className="font-medium">Optimisation géographique activée</span>
              {searchParams.localGlobalRatio && (
                <span className="ml-2">
                  ({Math.round(searchParams.localGlobalRatio * 100)}% local / {Math.round((1 - searchParams.localGlobalRatio) * 100)}% global)
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    );
  };

  const ActionButtons = () => (
    <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 pt-6">
      <Button
        onClick={onNewSearch || onBackToSearch}
        variant="outlined"
        startIcon={<RefreshCw />}
        className="text-purple-600 border-purple-300 hover:bg-purple-50 w-full sm:w-auto"
        sx={{ textTransform: 'none' }}
      >
        <span className="sm:hidden">Nouvelle recherche</span>
        <span className="hidden sm:inline">Nouvelle recherche d'inspiration</span>
      </Button>
      
      <Button
        onClick={onContinue}
        variant="contained"
        size="large"
        endIcon={<ArrowRight />}
        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-4 sm:px-8 py-3 w-full sm:w-auto"
        sx={{ textTransform: 'none' }}
      >
        <span className="sm:hidden">Continuer</span>
        <span className="hidden sm:inline">Continuer vers l'étape suivante</span>
      </Button>
    </div>
  );

  // RENDU PRINCIPAL
  return (
    <div className="space-y-6">
      <SynthesisHeader />

      {loadingStatus?.synthesis === 'loading' && <LoadingState />}

      {synthesis && (
        <>
          <SynthesisDisplay />
          <SourcesUsedSection />
          <SearchMetadataSection />
        </>
      )}

      {loadingStatus?.synthesis === 'error' && (
        <Alert severity="error">
          Une erreur est survenue lors de la génération de la synthèse. 
          Veuillez réessayer ou contacter le support.
        </Alert>
      )}

      {!synthesis && loadingStatus?.synthesis !== 'loading' && (
        <Alert severity="info">
          Aucune synthèse disponible. Retournez à la recherche pour en créer une nouvelle.
        </Alert>
      )}

      <ActionButtons />

      {/* Modal de partage chargé conditionnellement */}
    {ShareModalComponent && (
      <ShareModalComponent
        show={showShareModal}
        onClose={() => setShowShareModal(false)}
        synthesis={synthesis}
        selectedSources={selectedSources}
        userObjective={currentCompetence?.title || currentCompetence?.objectifOriginal || ''}
        searchParams={searchParams}
        metadata={metadata}
      />
    )}
    </div>
  );
}; 

export default InspirationSynthesis;